// TMOS Public Website Request Demo Page
// Date: January 27, 2025
// Purpose: Page for users to request a demo of TMOS

import React, { useState } from 'react';
import { PublicHeader } from '@/components/public/PublicHeader';
import { toast } from 'react-hot-toast';
import publicApi from '@/services/publicApi';
import { useLanguage } from '@/state/language/LanguageProvider';

export const RequestDemoPage: React.FC = () => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.company) {
            toast.error(t('website.demo.error'));
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await publicApi.post('/public/demo-request', {
                name: formData.name,
                email: formData.email,
                company: formData.company,
                phone: formData.phone || undefined,
                message: formData.message || undefined
            });

            if (response.data.success) {
                toast.success(t('website.demo.success'));
                setFormData({
                    name: '',
                    email: '',
                    company: '',
                    phone: '',
                    message: ''
                });
            } else {
                toast.error(response.data.message || t('website.demo.error'));
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || t('website.demo.error');
            toast.error(errorMessage);
            console.error('Error submitting demo request:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-[var(--color-secondary-bg)]">
            <PublicHeader />

            <main className="flex-1">
                <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16">
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-normal text-[var(--color-surface-text)] mb-4">
                            {t('website.demo.title')}
                        </h1>
                        <p className="text-xl text-[var(--color-secondary-text)] max-w-2xl mx-auto">
                            {t('website.demo.subtitle')}
                        </p>
                    </div>

                    <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-8">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                                    {t('website.demo.name')} <span className="text-[var(--color-error)]">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    required
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    className="w-full rounded-lg border border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:border-[var(--color-info)] focus:ring-2 focus:ring-[var(--color-info)]/20 outline-none transition-colors"
                                    placeholder={t('website.demo.name_placeholder')}
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                                    {t('website.demo.email')} <span className="text-[var(--color-error)]">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className="w-full rounded-lg border border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:border-[var(--color-info)] focus:ring-2 focus:ring-[var(--color-info)]/20 outline-none transition-colors"
                                    placeholder={t('website.demo.email_placeholder')}
                                />
                            </div>

                            <div>
                                <label htmlFor="company" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                                    {t('website.demo.company')} <span className="text-[var(--color-error)]">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="company"
                                    required
                                    value={formData.company}
                                    onChange={(e) => handleInputChange('company', e.target.value)}
                                    className="w-full rounded-lg border border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:border-[var(--color-info)] focus:ring-2 focus:ring-[var(--color-info)]/20 outline-none transition-colors"
                                    placeholder={t('website.demo.company_placeholder')}
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                                    {t('website.demo.phone')}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    className="w-full rounded-lg border border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:border-[var(--color-info)] focus:ring-2 focus:ring-[var(--color-info)]/20 outline-none transition-colors"
                                    placeholder={t('website.demo.phone_placeholder')}
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                                    {t('website.demo.message')}
                                </label>
                                <textarea
                                    id="message"
                                    rows={4}
                                    value={formData.message}
                                    onChange={(e) => handleInputChange('message', e.target.value)}
                                    className="w-full rounded-lg border border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] focus:border-[var(--color-info)] focus:ring-2 focus:ring-[var(--color-info)]/20 outline-none transition-colors resize-none"
                                    placeholder={t('website.demo.message_placeholder')}
                                />
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full rounded-lg bg-[var(--color-info)] px-6 py-3 text-base font-semibold text-[var(--color-accent-text)] shadow-sm hover:bg-[var(--color-info)]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? t('website.demo.submitting') : t('website.demo.submit')}
                                </button>
                            </div>
                        </form>

                        <div className="mt-8 pt-8 border-t border-[var(--color-border)]">
                            <p className="text-sm text-[var(--color-secondary-text)] text-center">
                                By submitting this form, you agree to our{' '}
                                <a href="/privacy" className="text-[var(--color-info)] hover:underline">{t('website.footer.privacy_policy')}</a>
                                {' '}and{' '}
                                <a href="/terms" className="text-[var(--color-info)] hover:underline">{t('website.footer.terms_of_service')}</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

        </div>
    );
};

